import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthUser } from "@/hooks/useAuth";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredUserType?: "candidate" | "company";
}

export function ProtectedRoute({ children, requiredUserType }: ProtectedRouteProps) {
  const location = useLocation();
  const { data: user, isLoading: userLoading } = useAuthUser();

  const { data: userType, isLoading: profileLoading } = useQuery({
    queryKey: ['user-type', user?.id],
    queryFn: async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', user!.id)
        .maybeSingle();
      return profile?.user_type ?? null;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10,
  });

  const isLoading = userLoading || (!!user && profileLoading);

  // Retorna null durante loading - o Suspense já mostra o PageLoader
  if (isLoading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredUserType && userType !== requiredUserType) {
    const redirectPath = userType === 'company' ? '/company/dashboard' : '/candidate/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}
